import { useCallback, useEffect, useRef, useState } from "react";
import {
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { formatDate } from "@/utils/format-date";
import {
  deleteDevolutionFile,
  getDevolutionFiles,
  uploadDevolutionFile,
} from "@/Api/devolution";
import { DevolutionFile } from "@/types/devolution";

/** Mesmo limite e formatos do backend (DevolutionFileConst) */
const MAX_SIZE_MB = 10;
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"];
const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

const REJECTED_STATUS = 3;

interface ReceiptModalProps {
  devolution: {
    uuid: string;
    value: number;
    devolution_status_id: number;
    files_count?: number;
    client?: { name: string } | null;
  };
  /** Financeiro: pode enviar e remover. Os demais só veem e baixam. */
  canManage: boolean;
  /** Avisa a tabela quando a quantidade de comprovantes muda */
  onFilesCountChange?: (count: number) => void;
}

const formatSize = (size: number | null) => {
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

const validateFile = (file: File): string | null => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return `"${file.name}": envie PDF, PNG ou JPG.`;
  }
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return `"${file.name}": o arquivo passa de ${MAX_SIZE_MB} MB.`;
  }
  return null;
};

const downloadFile = (file: DevolutionFile) => {
  const link = document.createElement("a");
  link.href = file.download_url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const ReceiptModal = ({
  devolution,
  canManage,
  onFilesCountChange,
}: ReceiptModalProps) => {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<DevolutionFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guardado em ref para não refazer a busca a cada render da tabela
  const onFilesCountChangeRef = useRef(onFilesCountChange);
  onFilesCountChangeRef.current = onFilesCountChange;

  const filesCount = devolution.files_count ?? 0;
  const hasFiles = filesCount > 0;
  const isRejected = devolution.devolution_status_id === REJECTED_STATUS;
  const canUpload = canManage && !isRejected && files.length < MAX_FILES;

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDevolutionFiles(devolution.uuid);
      if (response?.ok) {
        const list: DevolutionFile[] = response.data?.response || [];
        setFiles(list);
        onFilesCountChangeRef.current?.(list.length);
      }
    } finally {
      setLoading(false);
    }
  }, [devolution.uuid]);

  useEffect(() => {
    if (open) {
      setConfirmDeleteId(null);
      loadFiles();
    }
  }, [open, loadFiles]);

  const handleFiles = async (selected: FileList | File[]) => {
    const list = Array.from(selected);
    if (list.length === 0) return;

    const room = MAX_FILES - files.length;
    if (list.length > room) {
      toast.error(`Cada devolução aceita até ${MAX_FILES} comprovantes.`);
      return;
    }

    const errors = list.map(validateFile).filter(Boolean) as string[];
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    setUploading(true);
    let sent = 0;
    try {
      for (const file of list) {
        // O interceptor do axios já mostra o erro do servidor quando falha
        const response = await uploadDevolutionFile(devolution.uuid, file);
        if (response?.ok) sent++;
      }
      if (sent > 0) {
        toast.success(sent === 1 ? "Comprovante enviado!" : `${sent} comprovantes enviados!`);
      }
    } finally {
      setUploading(false);
      await loadFiles();
    }
  };

  const handleDelete = async (fileId: number) => {
    setDeletingId(fileId);
    try {
      const response = await deleteDevolutionFile(devolution.uuid, fileId);
      if (response?.ok) {
        toast.success("Comprovante removido.");
        await loadFiles();
      }
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // Vendedor sem comprovante: botão apagado, só com a dica
  const triggerDisabled = !canManage && !hasFiles;
  const triggerTitle = hasFiles
    ? `${filesCount} comprovante${filesCount > 1 ? "s" : ""} anexado${filesCount > 1 ? "s" : ""}`
    : canManage
      ? isRejected
        ? "Devolução rejeitada"
        : "Anexar comprovante"
      : "Sem comprovante";

  if (canManage && isRejected && !hasFiles) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        title={triggerTitle}
        aria-label={triggerTitle}
        disabled={triggerDisabled}
        onClick={() => setOpen(true)}
        className={cn(
          "relative inline-flex items-center justify-center size-7 rounded-md transition-colors",
          hasFiles
            ? "bg-gray-900 text-white hover:bg-gray-700 dark:bg-zinc-100 dark:text-zinc-900"
            : "border border-dashed border-gray-300 text-gray-400 hover:text-gray-700 hover:border-gray-500 dark:border-zinc-600",
          triggerDisabled && "opacity-40 cursor-not-allowed hover:text-gray-400 hover:border-gray-300"
        )}
      >
        <Paperclip className="size-4" />
        {filesCount > 1 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] leading-4 font-semibold">
            {filesCount}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="size-5" />
              Comprovante da devolução
            </DialogTitle>
            <DialogDescription>
              {devolution.client?.name} · {Mask.formatCurrency(devolution.value)}
            </DialogDescription>
          </DialogHeader>

          {canManage && !isRejected && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (canUpload) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                if (canUpload && !uploading) handleFiles(e.dataTransfer.files);
              }}
              onClick={() => canUpload && !uploading && inputRef.current?.click()}
              className={cn(
                "flex flex-col items-center justify-center gap-1 h-28 rounded-lg border-2 border-dashed text-center transition-colors",
                canUpload && !uploading
                  ? "cursor-pointer border-gray-300 bg-gray-50 hover:bg-gray-100 dark:bg-zinc-800 dark:border-zinc-600"
                  : "cursor-not-allowed border-gray-200 bg-gray-50/50 opacity-60",
                dragging && "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              )}
            >
              {uploading ? (
                <>
                  <Loader2 className="size-6 animate-spin text-gray-500" />
                  <p className="text-sm text-gray-500">Enviando...</p>
                </>
              ) : (
                <>
                  <Upload className="size-6 text-gray-500" />
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Clique para selecionar</span> ou arraste o arquivo aqui
                  </p>
                  <p className="text-xs text-gray-500">
                    {files.length >= MAX_FILES
                      ? `Limite de ${MAX_FILES} comprovantes atingido`
                      : `PDF, PNG ou JPG · até ${MAX_SIZE_MB} MB`}
                  </p>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
          )}

          <div className="space-y-2">
            {loading && files.length === 0 ? (
              <div className="flex justify-center py-6">
                <Loader2 className="size-6 animate-spin text-gray-500" />
              </div>
            ) : files.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-4">
                Nenhum comprovante anexado ainda.
              </p>
            ) : (
              files.map((file) => {
                const isImage = (file.mime_type || "").startsWith("image/");
                return (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 rounded-lg border p-3 bg-white dark:bg-zinc-900"
                  >
                    {isImage ? (
                      <ImageIcon className="size-5 shrink-0 text-blue-600" />
                    ) : (
                      <FileText className="size-5 shrink-0 text-red-600" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium" title={file.file_name}>
                        {file.file_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {[formatSize(file.size), formatDate(file.created_at), file.created_by?.name]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Abrir"
                        className="inline-flex size-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-zinc-800"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                      <button
                        type="button"
                        title="Baixar"
                        onClick={() => downloadFile(file)}
                        className="inline-flex size-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-zinc-800"
                      >
                        <Download className="size-4" />
                      </button>
                      {canManage &&
                        (confirmDeleteId === file.id ? (
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-8 px-2 text-xs"
                            disabled={deletingId === file.id}
                            onClick={() => handleDelete(file.id)}
                          >
                            {deletingId === file.id ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              "Confirmar"
                            )}
                          </Button>
                        ) : (
                          <button
                            type="button"
                            title="Remover"
                            onClick={() => setConfirmDeleteId(file.id)}
                            className="inline-flex size-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <p className="text-xs text-gray-500">
            Os links de download valem por 2 horas. Se expirar, é só abrir de novo.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReceiptModal;
